import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import InvoiceBuilder from './components/InvoiceBuilder';
import { createInAppNotification } from '../../utils/notifications';
import { sendSMS, buildJobStatusSMS } from '../../services/smsService';

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const generateDocuments = async (supabase, orgId, jobId, job, contact, orgName, businessType) => {
  const now = new Date();
  const dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  let servicePrice = Number(job?.labor_cost) || 0;
  const materialsCost = Number(job?.materials_cost) || 0;
  const subtotal = servicePrice + materialsCost;
  const taxRate = job?.tax_included ? (Number(job?.tax_rate) || 10) : 0;
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;
  const clientName = contact ? `${contact?.first_name} ${contact?.last_name}`?.trim() : 'Client';

  // Determine first document type based on business type
  const getFirstDoc = () => {
    const sf = job?.custom_fields || {};

    if (businessType === 'mobile_detailing') {
      return {
        org_id: orgId, job_id: jobId, doc_type: 'vehicle_inspection', status: 'draft',
        content: {
          title: 'Vehicle Inspection Checklist',
          job_title: job?.title, client_name: clientName,
          service_description: job?.description || job?.service_type,
          vehicle_type: sf?.vehicle_type || '',
          vehicle_make_model: sf?.vehicle_make_model || '',
          vehicle_year: sf?.vehicle_year || '',
          vehicle_color: sf?.vehicle_color || '',
          paint_condition: sf?.paint_condition || '',
          interior_condition: sf?.interior_condition || '',
          service_location: sf?.service_location || job?.location || '',
          scheduled_date: job?.scheduled_date || '',
          total_cost: total?.toFixed(2),
          company_name: orgName,
          checklist: JSON.stringify({
            exterior: { scratches: false, dents: false, chips: false, oxidation: false, swirl_marks: false },
            interior: { stains: false, tears: false, odor: false, pet_hair: false },
            glass: { chips: false, cracks: false },
            wheels: { curb_rash: false, brake_dust: false },
            notes: ''
          })
        }
      };
    }

    if (businessType === 'plumbing' || businessType === 'hvac' || businessType === 'electrical' || businessType === 'handyman') {
      return {
        org_id: orgId, job_id: jobId, doc_type: 'work_order', status: 'draft',
        content: {
          title: 'Work Order',
          work_order_number: `WO-${Date.now()?.toString()?.slice(-6)}`,
          job_title: job?.title, client_name: clientName,
          service_description: job?.description || job?.service_type,
          issue_type: sf?.issue_type || sf?.task_type || '',
          urgency: sf?.urgency || '',
          access_type: sf?.access_type || '',
          system_type: sf?.system_type || '',
          location: job?.location || '',
          scheduled_date: job?.scheduled_date || '',
          duration_hours: job?.duration_hours || 1,
          service_price: servicePrice?.toFixed(2),
          materials_cost: materialsCost?.toFixed(2),
          total_cost: total?.toFixed(2),
          company_name: orgName,
          terms: 'Work to be performed as described. Additional work requires written approval. Payment due upon completion.'
        }
      };
    }

    if (businessType === 'roofing' || businessType === 'general_contractor') {
      return {
        org_id: orgId, job_id: jobId, doc_type: 'contract', status: 'draft',
        content: {
          title: businessType === 'roofing' ? 'Roofing Contract' : 'Construction Contract',
          contract_number: `CON-${Date.now()?.toString()?.slice(-6)}`,
          job_title: job?.title, client_name: clientName,
          service_description: job?.description || job?.service_type,
          project_type: sf?.project_type || sf?.issue_type || '',
          project_scope: sf?.project_scope || '',
          permits_needed: sf?.permits_needed || '',
          timeline: sf?.timeline || '',
          roof_type: sf?.roof_type || '',
          insurance_claim: sf?.insurance_claim || '',
          location: job?.location || '',
          scheduled_date: job?.scheduled_date || '',
          service_price: servicePrice?.toFixed(2),
          materials_cost: materialsCost?.toFixed(2),
          total_cost: total?.toFixed(2),
          company_name: orgName,
          terms: 'Contractor agrees to perform work as specified. Client agrees to provide access and timely payment. All work warranted for 1 year against defects in workmanship. Materials warranty per manufacturer. Any changes to scope require written change order.'
        }
      };
    }

    if (businessType === 'cleaning' || businessType === 'landscaping' || businessType === 'pest_control' || businessType === 'painting' || businessType === 'pool_service') {
      return {
        org_id: orgId, job_id: jobId, doc_type: 'service_agreement', status: 'draft',
        content: {
          title: 'Service Agreement',
          agreement_number: `SA-${Date.now()?.toString()?.slice(-6)}`,
          job_title: job?.title, client_name: clientName,
          service_description: job?.description || job?.service_type,
          service_type: sf?.cleaning_type || sf?.service_needed || sf?.pest_type || sf?.paint_type || sf?.service_type_pool || job?.service_type || '',
          frequency: sf?.frequency || 'One-time',
          home_size: sf?.home_size || sf?.yard_size || '',
          location: job?.location || '',
          scheduled_date: job?.scheduled_date || '',
          service_price: servicePrice?.toFixed(2),
          materials_cost: materialsCost?.toFixed(2),
          total_cost: total?.toFixed(2),
          company_name: orgName,
          terms: 'Service Provider agrees to perform services as described. Client agrees to provide safe access. Payment due upon completion of each service visit. Cancellations require 24-hour notice.'
        }
      };
    }

    // Default: Agreement (moving + others)
    return {
      org_id: orgId, job_id: jobId, doc_type: 'agreement', status: 'draft',
      content: {
        title: 'Service Agreement',
        job_title: job?.title, client_name: clientName,
        service_description: job?.description || job?.service_type,
        move_from: sf?.move_from || job?.location || '',
        move_to: sf?.move_to || '',
        home_size: sf?.home_size || '',
        packing_needed: sf?.packing_needed || '',
        location: job?.location || '',
        scheduled_date: job?.scheduled_date || '',
        total_cost: total?.toFixed(2),
        company_name: orgName,
        terms: 'Standard service terms apply. Payment due upon completion. Service Provider is not responsible for pre-existing conditions. Client agrees to provide safe access to work area.'
      }
    };
  };

  const docs = [
    getFirstDoc(),
    {
      org_id: orgId, job_id: jobId, doc_type: 'estimate', status: 'draft',
      content: {
        title: 'Estimate',
        estimate_number: `EST-${Date.now()?.toString()?.slice(-6)}`,
        job_title: job?.title, client_name: clientName,
        service_description: job?.description || job?.service_type,
        location: job?.location || '',
        date: now?.toISOString()?.split('T')?.[0],
        service_price: servicePrice?.toFixed(2),
        materials_cost: materialsCost?.toFixed(2),
        subtotal: subtotal?.toFixed(2),
        tax: tax?.toFixed(2),
        tax_rate: taxRate,
        total: total?.toFixed(2),
        company_name: orgName
      }
    },
    {
      org_id: orgId, job_id: jobId, doc_type: 'invoice', status: 'unpaid',
      content: {
        title: 'Invoice',
        invoice_number: `INV-${Date.now()?.toString()?.slice(-6)}`,
        job_title: job?.title, client_name: clientName,
        service_description: job?.description || job?.service_type,
        location: job?.location || '',
        date: now?.toISOString()?.split('T')?.[0],
        due_date: dueDate?.toISOString()?.split('T')?.[0],
        duration: job?.duration_hours || 1,
        service_price: servicePrice?.toFixed(2),
        materials_cost: materialsCost?.toFixed(2),
        subtotal: subtotal?.toFixed(2),
        tax: tax?.toFixed(2),
        tax_rate: taxRate,
        total: total?.toFixed(2),
        payment_status: 'Unpaid',
        company_name: orgName
      }
    }
  ];

  await supabase?.from('documents')?.insert(docs);
};

// Dynamic job fields per service type
const JOB_SERVICE_FIELDS = {
  Moving: {
    icon: '🚚',
    hideLocation: true,
    fields: [
      { key: 'move_from', label: 'Moving From', type: 'text', placeholder: 'Full origin address', required: true },
      { key: 'move_to', label: 'Moving To', type: 'text', placeholder: 'Full destination address', required: true },
      { key: 'home_size', label: 'Home Size', type: 'select', options: ['Studio', '1 Bedroom', '2 Bedrooms', '3 Bedrooms', '4 Bedrooms', '5+ Bedrooms', 'Office/Commercial', 'Multi-unit / Duplex', 'Mobile home'] },
      { key: 'floors', label: 'Floors / Stairs', type: 'select', options: ['Ground floor (no stairs)', 'Has stairs at origin', 'Has stairs at destination', 'Both locations have stairs', 'Elevator available', 'No elevator — high floor'] },
      { key: 'packing_needed', label: 'Packing Service', type: 'select', options: ['No packing needed', 'Partial packing', 'Full packing service', 'Unpacking only', 'Pack & unpack'] },
      { key: 'storage_needed', label: 'Storage Needed', type: 'select', options: ['No storage needed', 'Short-term (< 1 month)', 'Long-term (1-6 months)', 'Long-term (6+ months)'] },
      { key: 'move_distance', label: 'Move Distance', type: 'select', options: ['Local (< 50 miles)', 'In-state (50-200 miles)', 'Long distance (200+ miles)', 'Cross-country', 'International'] },
      { key: 'special_items', label: 'Special Items', type: 'text', placeholder: 'Piano, safe, antiques, artwork, etc.' },
      { key: 'disassembly_needed', label: 'Disassembly / Assembly', type: 'select', options: ['Not needed', 'Furniture disassembly only', 'Furniture assembly only', 'Both disassembly & assembly'] },
      { key: 'access_notes', label: 'Access Notes', type: 'text', placeholder: 'Gate code, parking, elevator reservation, etc.' },
    ]
  },
  Plumbing: {
    icon: '🔧',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Leak repair', 'Drain clog / backup', 'Water heater repair', 'Water heater replacement', 'Pipe installation', 'Pipe burst / emergency', 'Sewer line issue', 'Toilet repair / replace', 'Faucet / fixture repair', 'Garbage disposal', 'Water pressure issue', 'Water filtration install', 'Backflow prevention', 'Other'], required: true },
      { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency (same day)', 'Urgent (within 24h)', 'Normal (this week)', 'Flexible / scheduled'] },
      { key: 'access_type', label: 'Access', type: 'select', options: ['Easy access', 'Crawl space', 'Basement', 'Attic', 'Behind walls / slab', 'Outdoor / yard'] },
      { key: 'water_shutoff', label: 'Water Shutoff Known?', type: 'select', options: ['Yes — main shutoff known', 'Yes — local shutoff only', 'No', 'Not sure'] },
      { key: 'property_type', label: 'Property Type', type: 'select', options: ['Single-family home', 'Condo / Apartment', 'Commercial building', 'Multi-unit / Duplex', 'Mobile home'] },
      { key: 'pipe_material', label: 'Pipe Material (if known)', type: 'select', options: ['Unknown', 'Copper', 'PVC', 'PEX', 'Galvanized steel', 'Cast iron', 'CPVC'] },
      { key: 'existing_damage', label: 'Visible Damage / Flooding?', type: 'select', options: ['No visible damage', 'Minor moisture / staining', 'Active leak', 'Flooding / major damage'] },
    ]
  },
  HVAC: {
    icon: '❄️',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['AC not cooling', 'Heater not working', 'New AC installation', 'New heater installation', 'Full system replacement', 'Maintenance / Tune-up', 'Duct cleaning', 'Duct repair / sealing', 'Thermostat issue', 'Refrigerant recharge', 'Blower / fan issue', 'Compressor issue', 'Air quality / filter', 'Other'], required: true },
      { key: 'system_type', label: 'System Type', type: 'select', options: ['Central AC + Heat', 'Mini-split (ductless)', 'Heat pump', 'Gas furnace', 'Electric furnace', 'Boiler / radiant heat', 'Window unit', 'Packaged unit', 'Unknown'] },
      { key: 'system_age', label: 'System Age', type: 'select', options: ['< 5 years', '5-10 years', '10-15 years', '15-20 years', '20+ years', 'Unknown'] },
      { key: 'sq_footage', label: 'Square Footage', type: 'text', placeholder: 'e.g. 1500 sq ft' },
      { key: 'last_service', label: 'Last Serviced', type: 'select', options: ['Within 1 year', '1-2 years ago', '2-5 years ago', '5+ years ago', 'Never / Unknown'] },
      { key: 'refrigerant_type', label: 'Refrigerant Type (if known)', type: 'select', options: ['Unknown', 'R-410A', 'R-22 (Freon)', 'R-32', 'R-454B'] },
      { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency (same day)', 'Urgent (within 24h)', 'Normal (this week)', 'Flexible / scheduled'] },
    ]
  },
  Handyman: {
    icon: '🛠️',
    fields: [
      { key: 'task_type', label: 'Task Type', type: 'select', options: ['Furniture assembly', 'TV / shelf mounting', 'Door repair / replace', 'Window repair / replace', 'Drywall patch / repair', 'Caulking / sealing', 'Shelf / cabinet install', 'Gutter cleaning / repair', 'Deck / porch repair', 'Fence repair / install', 'Tile repair / grout', 'Painting touch-up', 'Weatherstripping', 'Lock & hardware', 'Flooring repair', 'Attic / crawl space', 'Pressure washing', 'General repairs', 'Other'], required: true },
      { key: 'num_tasks', label: 'Number of Tasks', type: 'select', options: ['1 task (1-2 hrs)', '2-3 tasks (half day)', '4-6 tasks (full day)', '7+ tasks (multi-day)'] },
      { key: 'urgency', label: 'Urgency', type: 'select', options: ['ASAP (same day)', 'This week', 'Next week', 'Flexible'] },
      { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Rental property', 'Airbnb / Vacation rental', 'HOA / Community'] },
      { key: 'materials_needed', label: 'Materials', type: 'select', options: ['Client has all materials', 'Bring basic materials', 'Purchase materials (add to invoice)', 'Not sure yet'] },
      { key: 'tools_needed', label: 'Special Tools Needed?', type: 'select', options: ['Standard tools only', 'Ladder needed', 'Power tools needed', 'Specialty tools needed', 'Unknown'] },
      { key: 'access_instructions', label: 'Access Instructions', type: 'text', placeholder: 'Gate code, key location, call on arrival, etc.' },
    ]
  },
  Cleaning: {
    icon: '🧹',
    fields: [
      { key: 'cleaning_type', label: 'Cleaning Type', type: 'select', options: ['Standard clean', 'Deep clean', 'Move-in clean', 'Move-out clean', 'Post-construction clean', 'Commercial / office', 'Airbnb / vacation rental', 'Post-party / event', 'Hoarder / extreme clean'], required: true },
      { key: 'frequency', label: 'Frequency', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly', 'Every 3 months'] },
      { key: 'home_size', label: 'Space Size', type: 'select', options: ['Studio / 1 room', '1 bed / 1 bath', '2 bed / 1 bath', '2 bed / 2 bath', '3 bed / 2 bath', '4 bed / 3 bath', '5+ bedrooms', 'Commercial space (< 1000 sq ft)', 'Commercial space (1000-3000 sq ft)', 'Commercial space (3000+ sq ft)'] },
      { key: 'has_pets', label: 'Pets Present', type: 'select', options: ['No pets', 'Dog(s)', 'Cat(s)', 'Multiple pets', 'Allergies — pet-free products required'] },
      { key: 'supplies', label: 'Supplies', type: 'select', options: ['Client provides all supplies', 'We bring all supplies', 'Eco-friendly / green products', 'Fragrance-free products', 'Client provides vacuum, we bring rest'] },
      { key: 'special_requests', label: 'Special Requests', type: 'text', placeholder: 'Inside fridge, oven, windows, laundry, etc.' },
      { key: 'entry_method', label: 'Entry Method', type: 'select', options: ['Client present', 'Key provided', 'Lockbox / code', 'Doorman / concierge', 'Other'] },
    ]
  },
  Landscaping: {
    icon: '🌿',
    fields: [
      { key: 'service_needed', label: 'Service Needed', type: 'select', options: ['Lawn mowing', 'Lawn mowing + edging', 'Full yard maintenance', 'Landscaping design', 'Planting / flower beds', 'Tree trimming / pruning', 'Tree removal', 'Stump grinding', 'Irrigation install', 'Irrigation repair', 'Sod installation', 'Mulching', 'Leaf removal / cleanup', 'Snow removal', 'Seasonal cleanup', 'Ongoing maintenance plan'], required: true },
      { key: 'yard_size', label: 'Yard Size', type: 'select', options: ['Small (< 1/4 acre)', 'Medium (1/4 - 1/2 acre)', 'Large (1/2 - 1 acre)', 'Very large (1-2 acres)', 'Estate (2+ acres)'] },
      { key: 'frequency', label: 'Frequency', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly', 'Seasonal (spring/fall)', 'Annual'] },
      { key: 'gate_access', label: 'Gate / Access', type: 'select', options: ['Open access', 'Gate code needed', 'Must call ahead', 'Dog in yard — call before entry', 'Key / lockbox'] },
      { key: 'irrigation_present', label: 'Irrigation System?', type: 'select', options: ['No irrigation', 'Yes — working', 'Yes — needs repair', 'Unknown'] },
      { key: 'debris_disposal', label: 'Debris Disposal', type: 'select', options: ['Client handles disposal', 'We haul away debris', 'Leave in yard waste bins', 'Compost on-site'] },
    ]
  },
  Roofing: {
    icon: '🏠',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Leak repair', 'Full roof replacement', 'Partial replacement', 'Roof inspection', 'Storm / hail damage', 'Wind damage', 'Gutter repair', 'Gutter replacement', 'Fascia / soffit repair', 'Skylight install / repair', 'Flat roof repair', 'New construction install'], required: true },
      { key: 'roof_type', label: 'Roof Material', type: 'select', options: ['Asphalt shingles (3-tab)', 'Architectural shingles', 'Metal (standing seam)', 'Metal (corrugated)', 'Clay / concrete tile', 'Flat / TPO / EPDM', 'Wood shake / shingle', 'Slate', 'Unknown'] },
      { key: 'roof_age', label: 'Roof Age', type: 'select', options: ['< 5 years', '5-10 years', '10-15 years', '15-20 years', '20-25 years', '25+ years', 'Unknown'] },
      { key: 'stories', label: 'Stories / Height', type: 'select', options: ['1 story', '2 stories', '3 stories', '3+ stories / commercial'] },
      { key: 'roof_pitch', label: 'Roof Pitch', type: 'select', options: ['Low pitch (flat to 3/12)', 'Medium pitch (4/12 - 7/12)', 'Steep pitch (8/12+)', 'Unknown'] },
      { key: 'insurance_claim', label: 'Insurance Claim', type: 'select', options: ['No insurance claim', 'Claim already filed — approved', 'Claim filed — pending', 'Need help filing claim', 'Not sure'] },
      { key: 'ventilation', label: 'Ventilation / Attic', type: 'select', options: ['Good ventilation', 'Poor ventilation — needs improvement', 'Unknown', 'N/A'] },
    ]
  },
  Electrical: {
    icon: '⚡',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Panel upgrade / replacement', 'Circuit breaker issue', 'Outlet / switch install', 'GFCI / AFCI install', 'Lighting install / repair', 'Ceiling fan install', 'EV charger install', 'Generator hookup', 'Whole-home generator', 'Wiring / rewiring', 'Smoke / CO detector', 'Smart home / automation', 'Surge protection', 'Outdoor / landscape lighting', 'Emergency — power out', 'Other'], required: true },
      { key: 'panel_amps', label: 'Panel Size', type: 'select', options: ['60 amp (older home)', '100 amp', '150 amp', '200 amp', '400 amp', 'Unknown'] },
      { key: 'home_age', label: 'Home / Building Age', type: 'select', options: ['< 10 years', '10-20 years', '20-40 years', '40-60 years', '60+ years (older wiring)', 'Unknown'] },
      { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency (sparks / no power)', 'Urgent (within 24h)', 'Normal (this week)', 'Flexible / scheduled'] },
      { key: 'permit_required', label: 'Permit Required?', type: 'select', options: ['Yes — permit needed', 'No permit needed', 'Not sure'] },
      { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Industrial', 'Multi-unit'] },
    ]
  },
  'Pest Control': {
    icon: '🐛',
    fields: [
      { key: 'pest_type', label: 'Pest Type', type: 'select', options: ['Ants (general)', 'Fire ants', 'Cockroaches', 'Termites (subterranean)', 'Termites (drywood)', 'Rodents / Mice', 'Rats', 'Bed bugs', 'Wasps / Hornets', 'Bees (relocation)', 'Spiders', 'Fleas / Ticks', 'Mosquitoes', 'Wildlife / Squirrels', 'General pest prevention', 'Other'], required: true },
      { key: 'service_plan', label: 'Service Plan', type: 'select', options: ['One-time treatment', 'Monthly plan', 'Bi-monthly plan', 'Quarterly plan', 'Annual plan', 'Inspection only', 'Emergency treatment'] },
      { key: 'infestation_level', label: 'Severity', type: 'select', options: ['Minor (few sightings)', 'Moderate (regular sightings)', 'Severe (large infestation)', 'Unknown — needs inspection'] },
      { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial / restaurant', 'Warehouse / storage', 'Multi-unit / apartment', 'Outdoor / yard only'] },
      { key: 'chemical_sensitivity', label: 'Chemical Sensitivity', type: 'select', options: ['No restrictions', 'Pets present — pet-safe products', 'Children present — low-toxicity', 'Organic / eco-friendly only', 'Severe allergies — consult first'] },
      { key: 'previous_treatment', label: 'Previous Treatment?', type: 'select', options: ['No previous treatment', 'Yes — same company', 'Yes — different company', 'DIY treatment attempted'] },
    ]
  },
  Painting: {
    icon: '🖌️',
    fields: [
      { key: 'paint_type', label: 'Paint Type', type: 'select', options: ['Interior — walls', 'Interior — walls + trim', 'Interior — full room(s)', 'Exterior — full house', 'Exterior — trim only', 'Both interior & exterior', 'Cabinet painting / refinishing', 'Deck / fence staining', 'Epoxy floor coating', 'Commercial / office'], required: true },
      { key: 'rooms', label: 'Rooms / Areas', type: 'text', placeholder: 'e.g. Living room, 2 bedrooms, kitchen, hallway' },
      { key: 'sq_footage', label: 'Square Footage', type: 'text', placeholder: 'e.g. 1200 sq ft' },
      { key: 'paint_supplied', label: 'Paint Supply', type: 'select', options: ['Client supplies all paint', 'We supply paint (add to invoice)', 'Need color consultation', 'Client has color — we buy paint'] },
      { key: 'surface_condition', label: 'Surface Condition', type: 'select', options: ['Good (minor prep needed)', 'Fair (patching / sanding needed)', 'Poor (significant prep / priming)', 'New construction (bare drywall)'] },
      { key: 'coats_needed', label: 'Coats', type: 'select', options: ['1 coat (touch-up)', '2 coats (standard)', '3 coats (dark color / new surface)', 'Primer + 2 coats'] },
      { key: 'furniture_moving', label: 'Furniture Moving', type: 'select', options: ['Room is empty', 'Client moves furniture', 'We move furniture', 'Minimal furniture'] },
    ]
  },
  'Pool Service': {
    icon: '🏊',
    fields: [
      { key: 'service_type_pool', label: 'Service Type', type: 'select', options: ['Weekly maintenance', 'Bi-weekly maintenance', 'One-time cleaning', 'Green pool recovery', 'Equipment repair', 'Pump repair / replace', 'Filter cleaning / replace', 'Heater repair / install', 'Pool opening (spring)', 'Pool closing (winterize)', 'Chemical balance / shock', 'Algae treatment', 'Leak detection', 'Resurfacing / replaster', 'New pool installation'], required: true },
      { key: 'pool_type', label: 'Pool Type', type: 'select', options: ['In-ground (concrete)', 'In-ground (fiberglass)', 'In-ground (vinyl liner)', 'Above-ground', 'Spa / Hot tub only', 'Pool + Spa combo', 'Commercial pool'] },
      { key: 'pool_size', label: 'Pool Size', type: 'select', options: ['Small (< 10k gallons)', 'Medium (10-20k gallons)', 'Large (20-30k gallons)', 'Very large (30k+ gallons)', 'Unknown'] },
      { key: 'sanitizer_type', label: 'Sanitizer System', type: 'select', options: ['Chlorine (traditional)', 'Salt water / chlorinator', 'Bromine', 'UV / Ozone system', 'Unknown'] },
      { key: 'equipment_age', label: 'Equipment Age', type: 'select', options: ['< 3 years', '3-7 years', '7-12 years', '12+ years', 'Unknown'] },
      { key: 'current_condition', label: 'Current Condition', type: 'select', options: ['Clean — routine maintenance', 'Slightly cloudy', 'Green / algae present', 'Black algae', 'Equipment not working', 'Unknown'] },
    ]
  },
  'General Contractor': {
    icon: '🔨',
    fields: [
      { key: 'project_type', label: 'Project Type', type: 'select', options: ['Kitchen remodel', 'Bathroom remodel', 'Master suite addition', 'Room addition', 'Basement finish / remodel', 'Garage conversion / ADU', 'Deck / patio build', 'Whole home renovation', 'Commercial build-out', 'Tenant improvement', 'New construction', 'Other'], required: true },
      { key: 'project_scope', label: 'Project Budget', type: 'select', options: ['Small (< $5k)', 'Medium ($5k-$25k)', 'Large ($25k-$75k)', 'Major ($75k-$200k)', 'Custom ($200k+)'] },
      { key: 'permits_needed', label: 'Permits', type: 'select', options: ['No permits needed', 'Building permit required', 'Multiple permits required', 'Not sure — needs assessment'] },
      { key: 'timeline', label: 'Desired Timeline', type: 'select', options: ['ASAP', '1-2 months', '2-4 months', '4-6 months', '6-12 months', 'Flexible'] },
      { key: 'design_ready', label: 'Plans / Design', type: 'select', options: ['Architectural plans ready', 'Design in progress', 'Need design services', 'General idea only'] },
      { key: 'subcontractors', label: 'Subcontractors Needed', type: 'select', options: ['GC handles all subs', 'Client has some subs', 'Plumbing only', 'Electrical only', 'Plumbing + Electrical', 'All trades needed'] },
      { key: 'property_occupied', label: 'Property Occupied?', type: 'select', options: ['Yes — occupied during work', 'No — vacant during work', 'Partially occupied', 'Commercial — business open'] },
    ]
  },
  'Mobile Detailing': {
    icon: '🚗',
    fields: [
      { key: 'vehicle_type', label: 'Vehicle Type', type: 'select', options: ['Sedan', 'Coupe', 'SUV / Crossover', 'Pickup Truck', 'Van / Minivan', 'Sports Car', 'Luxury / Exotic', 'Motorcycle', 'Boat / RV', 'Fleet Vehicle'], required: true },
      { key: 'vehicle_make_model', label: 'Make & Model', type: 'text', placeholder: 'e.g. Toyota Camry, BMW X5' },
      { key: 'vehicle_year', label: 'Year', type: 'text', placeholder: 'e.g. 2021' },
      { key: 'vehicle_color', label: 'Color', type: 'text', placeholder: 'e.g. Pearl White, Midnight Black' },
      { key: 'paint_condition', label: 'Paint Condition', type: 'select', options: ['Excellent (minor swirls only)', 'Good (light scratches)', 'Fair (moderate scratches / oxidation)', 'Poor (heavy damage / deep scratches)'] },
      { key: 'interior_condition', label: 'Interior Condition', type: 'select', options: ['Clean (light dust)', 'Average (normal use)', 'Dirty (pet hair, stains)', 'Very dirty (heavy soiling / odor)'] },
      { key: 'service_location', label: 'Service Location', type: 'text', placeholder: 'Address where we come to you' },
      { key: 'ceramic_warranty', label: 'Ceramic Coating Warranty', type: 'select', options: ['N/A', '1 Year', '2 Years', '3 Years', '5 Years', 'Lifetime'] },
      { key: 'add_ons', label: 'Add-ons', type: 'text', placeholder: 'Engine bay, headlights, odor treatment, tint, etc.' },
    ]
  },
};

// Business-type → default service config key mapping
const BUSINESS_TYPE_CONFIG_MAP = {
  mobile_detailing: 'Mobile Detailing',
  moving: 'Moving',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  handyman: 'Handyman',
  cleaning: 'Cleaning',
  landscaping: 'Landscaping',
  roofing: 'Roofing',
  electrical: 'Electrical',
  pest_control: 'Pest Control',
  painting: 'Painting',
  pool_service: 'Pool Service',
  general_contractor: 'General Contractor',
};

// Smart config resolver: exact match first, then business-type fallback
const getServiceConfig = (serviceType, businessType) => {
  if (JOB_SERVICE_FIELDS?.[serviceType]) return JOB_SERVICE_FIELDS?.[serviceType];
  const fallbackKey = BUSINESS_TYPE_CONFIG_MAP?.[businessType];
  if (fallbackKey && JOB_SERVICE_FIELDS?.[fallbackKey]) return JOB_SERVICE_FIELDS?.[fallbackKey];
  return null;
};

const JobDetailDrawer = ({ job, isOpen, onClose, onStatusChange, orgName, onUpdate, orgPhone, orgEmail, organization }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isInvoiceBuilderOpen, setIsInvoiceBuilderOpen] = useState(false);

  useEffect(() => {
    if (isOpen && job?.id) {
      setLoadingDocs(true);
      supabase?.from('documents')?.select('*')?.eq('job_id', job?.id)?.then(({ data }) => { setDocuments(data || []); setLoadingDocs(false); });
    }
  }, [isOpen, job?.id]);

  useEffect(() => {
    if (job) {
      setEditForm({
        title: job?.title || '',
        service_type: job?.service_type || '',
        location: job?.location || '',
        pickup_address: job?.custom_fields?.pickup_address || job?.custom_fields?.move_from || '',
        delivery_address: job?.custom_fields?.delivery_address || job?.custom_fields?.move_to || '',
        scheduled_date: job?.scheduled_date || '',
        scheduled_time: job?.scheduled_time || '',
        duration_hours: job?.duration_hours || 1,
        labor_cost: job?.labor_cost || '',
        materials_cost: job?.materials_cost || '',
        tax_included: job?.tax_included || false,
        tax_rate: job?.tax_rate || 10,
        description: job?.description || '',
        notes: job?.notes || '',
        pricing_type: job?.custom_fields?.pricing_type || 'flat_rate',
        hourly_rate: job?.custom_fields?.hourly_rate || '',
        move_type: job?.move_type || 'local',
        cubic_feet: job?.cubic_feet || '',
        miles: job?.miles || '',
      });
      setIsEditing(false);
    }
  }, [job]);

  const handleSaveJob = async () => {
    setSaving(true);
    try {
      const isMoving = organization?.business_type === 'moving';
      const pricingType = editForm?.pricing_type || 'flat_rate';
      let servicePrice = Number(editForm?.labor_cost) || 0;

      // For hourly rate: calculate service price = rate × hours
      if (isMoving && pricingType === 'hourly_rate') {
        const rate = Number(editForm?.hourly_rate) || 0;
        const hours = Number(editForm?.duration_hours) || 1;
        servicePrice = rate * hours;
      }

      const materialsCost = Number(editForm?.materials_cost) || 0;
      const subtotal = servicePrice + materialsCost;
      const taxRate = editForm?.tax_included ? (Number(editForm?.tax_rate) || 10) : 0;
      const tax = subtotal * (taxRate / 100);
      const total = subtotal + tax;

      const updates = {
        title: editForm?.title,
        service_type: editForm?.service_type || null,
        location: isMoving ? (editForm?.pickup_address || null) : (editForm?.location || null),
        scheduled_date: editForm?.scheduled_date || null,
        scheduled_time: editForm?.scheduled_time || null,
        duration_hours: Number(editForm?.duration_hours) || 1,
        labor_cost: servicePrice,
        materials_cost: materialsCost,
        subtotal,
        total_cost: total,
        tax_included: editForm?.tax_included,
        tax_rate: taxRate,
        description: editForm?.description || null,
        notes: editForm?.notes || null,
        ...(isMoving && {
          move_type: editForm?.move_type || 'local',
          cubic_feet: editForm?.move_type === 'long_distance' ? (Number(editForm?.cubic_feet) || null) : null,
          miles: editForm?.move_type === 'long_distance' ? (Number(editForm?.miles) || null) : null,
          custom_fields: {
            ...(job?.custom_fields || {}),
            pickup_address: editForm?.pickup_address || '',
            delivery_address: editForm?.delivery_address || '',
            move_from: editForm?.pickup_address || '',
            move_to: editForm?.delivery_address || '',
            pricing_type: pricingType,
            hourly_rate: pricingType === 'hourly_rate' ? (Number(editForm?.hourly_rate) || 0) : null,
          }
        }),
        ...(!isMoving && {
          custom_fields: {
            ...(job?.custom_fields || {}),
          }
        }),
      };

      const { error } = await supabase?.from('jobs')?.update(updates)?.eq('id', job?.id);
      if (error) throw error;

      // Upsert calendar event when scheduled_date is set
      if (updates?.scheduled_date) {
        const startDt = updates?.scheduled_time
          ? `${updates?.scheduled_date}T${updates?.scheduled_time}`
          : `${updates?.scheduled_date}T09:00:00`;
        const endDt = new Date(new Date(startDt).getTime() + (Number(updates?.duration_hours) || 1) * 3600000)?.toISOString();

        // Check if a calendar event already exists for this job
        const { data: existingEvt } = await supabase
          ?.from('calendar_events')
          ?.select('id')
          ?.eq('job_id', job?.id)
          ?.is('deleted_at', null)
          ?.maybeSingle();

        if (existingEvt?.id) {
          await supabase?.from('calendar_events')?.update({
            title: updates?.title,
            description: updates?.description || '',
            start_datetime: startDt,
            end_datetime: endDt,
          })?.eq('id', existingEvt?.id);
        } else {
          await supabase?.from('calendar_events')?.insert({
            org_id: organization?.id || job?.org_id,
            job_id: job?.id,
            title: updates?.title,
            description: updates?.description || '',
            start_datetime: startDt,
            end_datetime: endDt,
            event_type: 'job',
            color: '#3B82F6',
          });
        }
      }

      toast?.success('Job updated!');
      onUpdate?.({ ...job, ...updates });
      setIsEditing(false);
    } catch (err) {
      toast?.error('Failed to update: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !job) return null;

  const statusCfg = STATUS_CONFIG?.[job?.status] || STATUS_CONFIG?.scheduled;

  const inputCls = 'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background';

  const editServicePrice = Number(editForm?.labor_cost) || 0;
  const editMaterialsCost = Number(editForm?.materials_cost) || 0;
  const editSubtotal = editServicePrice + editMaterialsCost;
  const editTaxAmt = editForm?.tax_included ? editSubtotal * ((Number(editForm?.tax_rate) || 10) / 100) : 0;
  const editTotal = editSubtotal + editTaxAmt;

  // For hourly rate preview
  const isMovingBiz = organization?.business_type === 'moving';
  const isHourlyRate = isMovingBiz && editForm?.pricing_type === 'hourly_rate';
  const editHourlyRate = Number(editForm?.hourly_rate) || 0;
  const editHours = Number(editForm?.duration_hours) || 1;
  const editComputedServicePrice = isHourlyRate ? editHourlyRate * editHours : editServicePrice;
  const editComputedSubtotal = editComputedServicePrice + editMaterialsCost;
  const editComputedTaxAmt = editForm?.tax_included ? editComputedSubtotal * ((Number(editForm?.tax_rate) || 10) / 100) : 0;
  const editComputedTotal = editComputedSubtotal + editComputedTaxAmt;

  const printDocument = async (doc) => {
    const c = doc?.content;
    const orgId = organization?.id;

    // Try to fetch the org's custom template for this doc type
    let customTemplateContent = null;
    let templateType = doc?.doc_type;
    if (orgId && templateType && !['vehicle_inspection', 'work_order', 'contract', 'service_agreement']?.includes(templateType)) {
      try {
        const { data: tmplData } = await supabase
          ?.from('templates')
          ?.select('content, name, logo_url, show_logo, font_style, payment_terms')
          ?.eq('org_id', orgId)
          ?.eq('template_type', templateType)
          ?.is('deleted_at', null)
          ?.order('is_default', { ascending: false })
          ?.order('updated_at', { ascending: false })
          ?.limit(1)
          ?.maybeSingle();
        if (tmplData?.content) {
          customTemplateContent = tmplData?.content;
        }
      } catch (_e) {}
    }

    let html;

    if (customTemplateContent) {
      const now = new Date();
      const dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      // Build client name from job.client (joined relation)
      const clientFirstName = job?.client?.first_name || '';
      const clientLastName = job?.client?.last_name || '';
      const clientFullName = [clientFirstName, clientLastName]?.filter(Boolean)?.join(' ') || c?.client_name || '';

      // Build all variables from job data
      const cf = job?.custom_fields || {};
      const pricingType = cf?.pricing_type || 'flat_rate';
      const hourlyRate = Number(cf?.hourly_rate) || 0;
      const durationHours = Number(job?.duration_hours) || 1;
      const laborCost = pricingType === 'hourly_rate' ? (hourlyRate * durationHours) : (Number(job?.labor_cost) || 0);
      const materialsCostVal = Number(job?.materials_cost) || 0;
      const subtotalVal = laborCost + materialsCostVal;
      const taxRateVal = job?.tax_included ? (Number(job?.tax_rate) || 0) : 0;
      const taxVal = subtotalVal * (taxRateVal / 100);
      const totalVal = subtotalVal + taxVal;

      const originAddress = cf?.pickup_address || cf?.move_from || job?.location || '';
      const destAddress = cf?.delivery_address || cf?.move_to || '';

      const formatDate = (d) => {
        if (!d) return '';
        try { return new Date(d)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return d; }
      };

      const variables = {
        // Company
        company_name: c?.company_name || orgName || organization?.name || '',
        // Client
        client_name: clientFullName,
        client_phone: job?.client?.phone || c?.client_phone || '',
        client_email: job?.client?.email || c?.client_email || '',
        client_address: originAddress,
        // Job
        job_number: job?.id ? String(job?.id)?.slice(-6)?.toUpperCase() : '',
        job_title: job?.title || c?.job_title || '',
        job_date: formatDate(job?.scheduled_date) || formatDate(now),
        job_status: job?.status ? (job?.status?.charAt(0)?.toUpperCase() + job?.status?.slice(1)?.replace(/_/g, ' ')) : '',
        service_description: job?.description || job?.service_type || c?.service_description || '',
        service_type: job?.service_type || c?.service_type || '',
        location: job?.location || c?.location || '',
        scheduled_date: formatDate(job?.scheduled_date) || c?.scheduled_date || '',
        delivery_date: formatDate(cf?.delivery_date) || c?.delivery_date || '',
        due_date: c?.due_date ? formatDate(c?.due_date) : formatDate(dueDate),
        date: formatDate(now),
        // Pricing
        hourly_rate: hourlyRate > 0 ? hourlyRate?.toFixed(2) : (c?.hourly_rate || ''),
        duration: String(durationHours),
        labor_cost: laborCost?.toFixed(2),
        materials_cost: materialsCostVal?.toFixed(2),
        subtotal: subtotalVal?.toFixed(2),
        tax: taxVal?.toFixed(2),
        total_cost: totalVal?.toFixed(2),
        total: totalVal?.toFixed(2),
        max_amount_due: c?.max_amount_due || totalVal?.toFixed(2),
        balance_due: c?.balance_due || '0.00',
        payment_status: c?.payment_status || job?.payment_status || 'Unpaid',
        payment_terms: c?.payment_terms || 'Due upon completion',
        invoice_number: c?.invoice_number || '',
        estimate_number: c?.estimate_number || '',
        // Moving / addresses
        origin_address: originAddress,
        destination_address: destAddress,
        move_from: originAddress,
        move_to: destAddress,
        from_address: originAddress,
        from_phone: job?.client?.phone || '',
        to_address: destAddress,
        to_phone: '',
        pickup_address: originAddress,
        delivery_address: destAddress,
        // Workers
        crew_size: cf?.num_movers || cf?.crew_size || String(durationHours) || '',
        num_movers: cf?.num_movers || '',
        num_trucks: cf?.num_trucks || '',
        assigned_workers: job?.assigned?.full_name || c?.assigned_workers || '',
        start_time: job?.scheduled_time || c?.start_time || '',
        // Other
        home_size: cf?.home_size || c?.home_size || '',
        issue_type: cf?.issue_type || cf?.task_type || c?.issue_type || '',
        system_type: cf?.system_type || c?.system_type || '',
        frequency: cf?.frequency || c?.frequency || '',
        project_type: cf?.project_type || c?.project_type || '',
        vehicle_type: cf?.vehicle_type || c?.vehicle_type || '',
        vehicle_make_model: cf?.vehicle_make_model || c?.vehicle_make_model || '',
        vehicle_year: cf?.vehicle_year || c?.vehicle_year || '',
        vehicle_color: cf?.vehicle_color || c?.vehicle_color || '',
        paint_condition: cf?.paint_condition || c?.paint_condition || '',
        service_location: cf?.service_location || job?.location || '',
      };

      // Process template: strip var-chip spans first, then replace variables
      let rendered = customTemplateContent;

      // Strip var-chip span wrappers (keep inner text which is the {{variable}})
      rendered = rendered?.replace(/<span[^>]*class="var-chip"[^>]*contenteditable="false"[^>]*>([\s\S]*?)<\/span>/g, '$1');
      rendered = rendered?.replace(/<span[^>]*class="var-chip"[^>]*>([\s\S]*?)<\/span>/g, '$1');

      // Replace all {{variable}} with actual values
      Object.entries(variables)?.forEach(([key, val]) => {
        rendered = rendered?.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val != null ? String(val) : '');
      });

      // Any remaining unreplaced variables — leave as empty string
      rendered = rendered?.replace(/\{\{[^}]+\}\}/g, '');

      const typeLabel = templateType === 'agreement' ? 'SERVICE AGREEMENT'
        : templateType === 'estimate' ? 'ESTIMATE'
        : templateType === 'invoice' ? 'INVOICE'
        : templateType?.charAt(0)?.toUpperCase() + templateType?.slice(1);

      const FONT_MAP = {
        professional: "'Helvetica Neue', Arial, sans-serif",
        modern: "'DM Sans', sans-serif",
        classic: "Georgia, 'Times New Roman', serif",
        minimal: "'Courier New', Courier, monospace",
      };
      const fontFamily = FONT_MAP?.['professional'];

      html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${typeLabel} — ${clientFullName || ''}</title>
  <style>
    @media print { body { margin: 0; } .no-print { display: none; } }
    * { box-sizing: border-box; }
    body { font-family: ${fontFamily}; color: #1a1a1a; background: #fff; margin: 0; padding: 0; }
    .container { max-width: 720px; margin: 30px auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
    .doc-header { padding: 20px 32px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: flex-start; }
    .doc-header-left { display: flex; align-items: center; gap: 12px; }
    .doc-header-company { font-size: 14px; font-weight: 600; color: #111827; }
    .doc-header-subtitle { font-size: 12px; color: #6b7280; margin-top: 2px; }
    .doc-header-type { font-size: 22px; font-weight: 700; color: #111827; text-align: right; }
    .doc-header-date { font-size: 12px; color: #6b7280; text-align: right; margin-top: 2px; }
    .doc-body { padding: 24px 32px; min-height: 400px; font-size: 13px; line-height: 1.6; color: #374151; }
    .doc-footer { padding: 12px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; }
    .doc-footer span { font-size: 11px; color: #9ca3af; }
    .doc-body h1 { font-size: 1.4rem; font-weight: 700; margin: 10px 0 5px; color: #111827; }
    .doc-body h2 { font-size: 1.1rem; font-weight: 600; margin: 8px 0 4px; color: #1f2937; }
    .doc-body h3 { font-size: 0.95rem; font-weight: 600; margin: 6px 0 3px; color: #374151; }
    .doc-body p { margin: 3px 0; }
    .doc-body ul { list-style: disc; padding-left: 18px; margin: 4px 0; }
    .doc-body ol { list-style: decimal; padding-left: 18px; margin: 4px 0; }
    .doc-body li { margin: 2px 0; }
    .doc-body table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    .doc-body td, .doc-body th { border: 1px solid #e5e7eb; padding: 5px 8px; font-size: 12px; }
    .doc-body th { background: #f9fafb; font-weight: 600; }
    .doc-body hr { border: none; border-top: 1px solid #e5e7eb; margin: 8px 0; }
    .doc-body strong, .doc-body b { font-weight: 700; }
    .doc-body em, .doc-body i { font-style: italic; }
    .doc-body u { text-decoration: underline; }
  </style>
</head>
<body>
  <div class="container">
    <div class="doc-header">
      <div class="doc-header-left">
        <div>
          <div class="doc-header-company">${variables?.company_name}</div>
          <div class="doc-header-subtitle">${variables?.payment_terms}</div>
        </div>
      </div>
      <div>
        <div class="doc-header-type">${typeLabel}</div>
        <div class="doc-header-date">${formatDate(now)}</div>
      </div>
    </div>
    <div class="doc-body">${rendered}</div>
    <div class="doc-footer">
      <span>${variables?.company_name} — Confidential</span>
      <span>${new Date()?.toLocaleDateString()}</span>
    </div>
  </div>
</body>
</html>`;
    } else {
      // Specialized print layouts per doc type
      if (doc?.doc_type === 'vehicle_inspection') {
        const accent = '#f59e0b';
        html = `<!DOCTYPE html><html><head><title>Vehicle Inspection — ${c?.client_name || ''}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:680px;margin:30px auto;padding:20px;color:#1a1a1a}
  h1{font-size:20px;font-weight:700;border-bottom:3px solid ${accent};padding-bottom:8px;margin-bottom:16px;color:${accent}}
  h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin:16px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
  .info-item{background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 12px}
  .info-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#92400e;margin-bottom:2px}
  .info-value{font-size:13px;font-weight:600;color:#111827}
  .desc-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:13px;min-height:60px;margin-bottom:12px}
  .price-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}
  .price-table td{padding:6px 10px;border:1px solid #e5e7eb}
  .price-table .total-row{font-weight:700;background:#fffbeb}
  .sig-block{margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:24px}
  .sig-line{border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280}
  @media print{body{margin:0}}
</style></head><body>
<h1>🚗 Vehicle Inspection Checklist</h1>
<div style="display:flex;justify-content:space-between;margin-bottom:12px;font-size:12px;color:#6b7280">
  <span><strong>${c?.company_name || orgName || ''}</strong></span>
  <span>Date: ${c?.scheduled_date || new Date()?.toLocaleDateString()}</span>
</div>
<div class="info-grid">
  <div class="info-item"><div class="info-label">Client</div><div class="info-value">${c?.client_name || '—'}</div></div>
  <div class="info-item"><div class="info-label">Service</div><div class="info-value">${c?.service_description || '—'}</div></div>
  <div class="info-item"><div class="info-label">Vehicle</div><div class="info-value">${[c?.vehicle_year, c?.vehicle_make_model]?.filter(Boolean)?.join(' ') || '—'}</div></div>
  <div class="info-item"><div class="info-label">Type / Color</div><div class="info-value">${[c?.vehicle_type, c?.vehicle_color]?.filter(Boolean)?.join(' · ') || '—'}</div></div>
  <div class="info-item"><div class="info-label">Paint Condition</div><div class="info-value">${c?.paint_condition || '—'}</div></div>
  <div class="info-item"><div class="info-label">Interior Condition</div><div class="info-value">${c?.interior_condition || '—'}</div></div>
  <div class="info-item"><div class="info-label">Service Location</div><div class="info-value">${c?.service_location || '—'}</div></div>
  <div class="info-item"><div class="info-label">Total Cost</div><div class="info-value" style="color:${accent}">$${c?.total_cost || '0.00'}</div></div>
</div>
<h2>Exterior Inspection</h2>
<table><tr><th>Item</th><th style="width:80px;text-align:center">Pre-existing</th></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Scratches</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.exterior?.scratches ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Dents / Dings</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.exterior?.dents ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Paint Chips</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.exterior?.chips ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Oxidation / Fading</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.exterior?.oxidation ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Swirl Marks</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.exterior?.swirl_marks ? '✅' : '☐'}</td></tr>
</table>
<h2>Interior Inspection</h2>
<table><tr><th>Item</th><th style="width:80px;text-align:center">Pre-existing</th></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Stains</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.interior?.stains ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Tears / Damage</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.interior?.tears ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Odor</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.interior?.odor ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Pet Hair</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.interior?.pet_hair ? '✅' : '☐'}</td></tr>
</table>
<h2>Glass & Wheels</h2>
<table><tr><th>Item</th><th style="width:80px;text-align:center">Pre-existing</th></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Glass Chips</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.glass?.chips ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Glass Cracks</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.glass?.cracks ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Curb Rash (Wheels)</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.wheels?.curb_rash ? '✅' : '☐'}</td></tr>
<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">Heavy Brake Dust</td><td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;">${c?.wheels?.brake_dust ? '✅' : '☐'}</td></tr>
</table>
<h2>Technician Notes</h2>
<div style="border:1px solid #e5e7eb;border-radius:6px;min-height:60px;padding:10px;font-size:13px;color:#374151;background:#fafafa">${c?.notes || '&nbsp;'}</div>
<div class="sig-block">
  <div><div class="sig-line">Technician Signature &amp; Date</div></div>
  <div><div class="sig-line">Client Signature &amp; Date</div></div>
</div>
<p style="margin-top:24px;font-size:10px;color:#9ca3af;text-align:center">By signing, client acknowledges pre-existing conditions noted above. ${c?.company_name || orgName || ''} is not responsible for pre-existing damage.</p>
</body></html>`;

      } else if (doc?.doc_type === 'work_order') {
        const accent = '#f59e0b';
        html = `<!DOCTYPE html><html><head><title>Work Order — ${c?.client_name || ''}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:680px;margin:30px auto;padding:20px;color:#1a1a1a}
  h1{font-size:20px;font-weight:700;border-bottom:3px solid ${accent};padding-bottom:8px;margin-bottom:16px;color:${accent}}
  h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin:16px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
  .info-item{background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 12px}
  .info-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#92400e;margin-bottom:2px}
  .info-value{font-size:13px;font-weight:600;color:#111827}
  .desc-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:13px;min-height:60px;margin-bottom:12px}
  .price-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}
  .price-table td{padding:6px 10px;border:1px solid #e5e7eb}
  .price-table .total-row{font-weight:700;background:#fffbeb}
  .sig-block{margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:24px}
  .sig-line{border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280}
  @media print{body{margin:0}}
</style></head><body>
<h1>🔧 Work Order</h1>
<div style="display:flex;justify-content:space-between;margin-bottom:12px;font-size:12px;color:#6b7280">
  <span><strong>${c?.company_name || orgName || ''}</strong></span>
  <span>WO# ${c?.work_order_number || '—'} &nbsp;|&nbsp; Date: ${c?.scheduled_date || new Date()?.toLocaleDateString()}</span>
</div>
<div class="info-grid">
  <div class="info-item"><div class="info-label">Client</div><div class="info-value">${c?.client_name || '—'}</div></div>
  <div class="info-item"><div class="info-label">Job</div><div class="info-value">${c?.job_title || '—'}</div></div>
  <div class="info-item"><div class="info-label">Issue Type</div><div class="info-value">${c?.issue_type || '—'}</div></div>
  <div class="info-item"><div class="info-label">Urgency</div><div class="info-value">${c?.urgency || '—'}</div></div>
  <div class="info-item"><div class="info-label">Location</div><div class="info-value">${c?.location || '—'}</div></div>
  <div class="info-item"><div class="info-label">Duration</div><div class="info-value">${c?.duration_hours || 1} hr(s)</div></div>
</div>
<h2>Work Description</h2>
<div class="desc-box">${c?.service_description || '&nbsp;'}</div>
<h2>Pricing</h2>
<table class="price-table">
  <tr><td>Service / Labor</td><td style="text-align:right">$${c?.service_price || '0.00'}</td></tr>
  <tr><td>Materials / Parts</td><td style="text-align:right">$${c?.materials_cost || '0.00'}</td></tr>
  <tr class="total-row"><td>TOTAL</td><td style="text-align:right">$${c?.total_cost || '0.00'}</td></tr>
</table>
<h2>Terms</h2>
<p style="font-size:12px;color:#6b7280">${c?.terms || ''}</p>
<div class="sig-block">
  <div><div class="sig-line">Technician Signature &amp; Date</div></div>
  <div><div class="sig-line">Client Approval &amp; Date</div></div>
</div>
</body></html>`;

      } else if (doc?.doc_type === 'contract') {
        const accent = '#dc2626';
        html = `<!DOCTYPE html><html><head><title>Contract — ${c?.client_name || ''}</title>
<style>
  body{font-family:Georgia,serif;max-width:680px;margin:30px auto;padding:20px;color:#1a1a1a}
  h1{font-size:22px;font-weight:700;border-bottom:3px solid ${accent};padding-bottom:8px;margin-bottom:16px;color:${accent}}
  h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin:16px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
  .info-item{background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:8px 12px}
  .info-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#991b1b;margin-bottom:2px}
  .info-value{font-size:13px;font-weight:600;color:#111827}
  .terms-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:12px;color:#374151;line-height:1.6;margin-bottom:16px}
  .price-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}
  .price-table td{padding:6px 10px;border:1px solid #e5e7eb}
  .price-table .total-row{font-weight:700;background:#fef2f2}
  .sig-block{margin-top:40px;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig-line{border-top:2px solid #374151;padding-top:8px;font-size:11px;color:#6b7280}
  @media print{body{margin:0}}
</style></head><body>
<h1>📋 ${c?.title || 'Contract'}</h1>
<div style="display:flex;justify-content:space-between;margin-bottom:12px;font-size:12px;color:#6b7280">
  <span><strong>${c?.company_name || orgName || ''}</strong></span>
  <span>Contract# ${c?.contract_number || '—'} &nbsp;|&nbsp; Date: ${c?.scheduled_date || new Date()?.toLocaleDateString()}</span>
</div>
<div class="info-grid">
  <div class="info-item"><div class="info-label">Client</div><div class="info-value">${c?.client_name || '—'}</div></div>
  <div class="info-item"><div class="info-label">Project</div><div class="info-value">${c?.job_title || '—'}</div></div>
  <div class="info-item"><div class="info-label">Project Type</div><div class="info-value">${c?.project_type || '—'}</div></div>
  <div class="info-item"><div class="info-label">Scope / Budget</div><div class="info-value">${c?.project_scope || '—'}</div></div>
  <div class="info-item"><div class="info-label">Permits</div><div class="info-value">${c?.permits_needed || '—'}</div></div>
  <div class="info-item"><div class="info-label">Timeline</div><div class="info-value">${c?.timeline || '—'}</div></div>
  <div class="info-item"><div class="info-label">Location</div><div class="info-value">${c?.location || '—'}</div></div>
  <div class="info-item"><div class="info-label">Insurance Claim</div><div class="info-value">${c?.insurance_claim || 'N/A'}</div></div>
</div>
<h2>Scope of Work</h2>
<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:13px;min-height:60px;margin-bottom:12px">${c?.service_description || '&nbsp;'}</div>
<h2>Contract Value</h2>
<table class="price-table">
  <tr><td>Labor / Service</td><td style="text-align:right">$${c?.service_price || '0.00'}</td></tr>
  <tr><td>Materials</td><td style="text-align:right">$${c?.materials_cost || '0.00'}</td></tr>
  <tr class="total-row"><td>CONTRACT TOTAL</td><td style="text-align:right">$${c?.total_cost || '0.00'}</td></tr>
</table>
<h2>Terms & Conditions</h2>
<div class="terms-box">${c?.terms || ''}</div>
<div class="sig-block">
  <div><div class="sig-line">Contractor Signature &amp; Date</div><div style="margin-top:4px;font-size:11px;color:#9ca3af">${c?.company_name || ''}</div></div>
  <div><div class="sig-line">Client Signature &amp; Date</div><div style="margin-top:4px;font-size:11px;color:#9ca3af">${c?.client_name || ''}</div></div>
</div>
</body></html>`;

      } else if (doc?.doc_type === 'service_agreement') {
        const accent = '#059669';
        html = `<!DOCTYPE html><html><head><title>Service Agreement — ${c?.client_name || ''}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:680px;margin:30px auto;padding:20px;color:#1a1a1a}
  h1{font-size:20px;font-weight:700;border-bottom:3px solid ${accent};padding-bottom:8px;margin-bottom:16px;color:${accent}}
  h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin:16px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
  .info-item{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:8px 12px}
  .info-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#065f46;margin-bottom:2px}
  .info-value{font-size:13px;font-weight:600;color:#111827}
  .terms-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:12px;color:#374151;line-height:1.6;margin-bottom:16px}
  .price-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}
  .price-table td{padding:6px 10px;border:1px solid #e5e7eb}
  .price-table .total-row{font-weight:700;background:#f0fdf4}
  .sig-block{margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:24px}
  .sig-line{border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280}
  @media print{body{margin:0}}
</style></head><body>
<h1>✅ Service Agreement</h1>
<div style="display:flex;justify-content:space-between;margin-bottom:12px;font-size:12px;color:#6b7280">
  <span><strong>${c?.company_name || orgName || ''}</strong></span>
  <span>SA# ${c?.agreement_number || '—'} &nbsp;|&nbsp; Date: ${c?.scheduled_date || new Date()?.toLocaleDateString()}</span>
</div>
<div class="info-grid">
  <div class="info-item"><div class="info-label">Client</div><div class="info-value">${c?.client_name || '—'}</div></div>
  <div class="info-item"><div class="info-label">Service</div><div class="info-value">${c?.service_type || c?.service_description || '—'}</div></div>
  <div class="info-item"><div class="info-label">Frequency</div><div class="info-value">${c?.frequency || 'One-time'}</div></div>
  <div class="info-item"><div class="info-label">Property Size</div><div class="info-value">${c?.home_size || '—'}</div></div>
  <div class="info-item"><div class="info-label">Location</div><div class="info-value">${c?.location || '—'}</div></div>
  <div class="info-item"><div class="info-label">Scheduled Date</div><div class="info-value">${c?.scheduled_date || '—'}</div></div>
</div>
<h2>Service Description</h2>
<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px;font-size:13px;min-height:50px;margin-bottom:12px">${c?.service_description || '&nbsp;'}</div>
<h2>Pricing</h2>
<table class="price-table">
  <tr><td>Service Price</td><td style="text-align:right">$${c?.service_price || '0.00'}</td></tr>
  <tr><td>Materials / Supplies</td><td style="text-align:right">$${c?.materials_cost || '0.00'}</td></tr>
  <tr class="total-row"><td>TOTAL</td><td style="text-align:right">$${c?.total_cost || '0.00'}</td></tr>
</table>
<h2>Terms</h2>
<div class="terms-box">${c?.terms || ''}</div>
<div class="sig-block">
  <div><div class="sig-line">Service Provider Signature &amp; Date</div></div>
  <div><div class="sig-line">Client Signature &amp; Date</div></div>
</div>
</body></html>`;

      } else {
        // Generic fallback (agreement, estimate, invoice)
        html = `<html><head><title>${c?.title}</title><style>body{font-family:Arial,sans-serif;max-width:600px;margin:40px auto;padding:20px;color:#333}h1{color:#1a1a1a;border-bottom:2px solid #333;padding-bottom:10px}table{width:100%;border-collapse:collapse;margin:20px 0}td,th{padding:8px;border:1px solid #ddd;text-align:left}th{background:#f5f5f5}.total{font-weight:bold;font-size:1.1em}.sig{margin-top:40px;border-top:1px solid #333;padding-top:10px}</style></head><body>`;
        html += `<h1>${c?.title}</h1>`;
        html += `<p><strong>From:</strong> ${c?.company_name || orgName}</p>`;
        html += `<p><strong>To:</strong> ${c?.client_name}</p>`;
        if (c?.invoice_number) html += `<p><strong>Invoice #:</strong> ${c?.invoice_number}</p>`;
        if (c?.estimate_number) html += `<p><strong>Estimate #:</strong> ${c?.estimate_number}</p>`;
        if (c?.date) html += `<p><strong>Date:</strong> ${c?.date}</p>`;
        if (c?.due_date) html += `<p><strong>Due Date:</strong> ${c?.due_date}</p>`;
        html += `<p><strong>Service:</strong> ${c?.service_description}</p>`;
        if (c?.location) html += `<p><strong>Location:</strong> ${c?.location}</p>`;
        if (c?.move_from) html += `<p><strong>Moving From:</strong> ${c?.move_from}</p>`;
        if (c?.move_to) html += `<p><strong>Moving To:</strong> ${c?.move_to}</p>`;
        if (c?.service_price) {
          html += `<table><tr><th>Item</th><th>Amount</th></tr>`;
          html += `<tr><td>Service Price</td><td>$${c?.service_price}</td></tr>`;
          html += `<tr><td>Materials / Supplies</td><td>$${c?.materials_cost}</td></tr>`;
          html += `<tr><td>Subtotal</td><td>$${c?.subtotal}</td></tr>`;
          if (Number(c?.tax) > 0) html += `<tr><td>Tax (${c?.tax_rate || 10}%)</td><td>$${c?.tax}</td></tr>`;
          html += `<tr class="total"><td>TOTAL</td><td>$${c?.total}</td></tr></table>`;
        }
        if (c?.terms) html += `<p><strong>Terms:</strong> ${c?.terms}</p>`;
        if (c?.payment_status) html += `<p><strong>Payment Status:</strong> ${c?.payment_status}</p>`;
        if (doc?.doc_type === 'agreement') html += `<div class="sig"><p>Signature: _______________________  Date: __________</p><p>Client: ${c?.client_name}</p></div>`;
        html += '</body></html>';
      }
    }

    const w = window.open('', '_blank');
    w?.document?.write(html);
    w?.document?.close();
    w?.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{job?.title}</h2>
            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium mt-1 ${statusCfg?.color}`}>{statusCfg?.label}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsInvoiceBuilderOpen(true)}
              className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              <Icon name="Receipt" size={13} />
              <span>Invoice</span>
            </button>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="Pencil" size={13} />
                <span>Edit</span>
              </button>
            ) : (
              <button
                onClick={() => { setIsEditing(false); }}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={13} />
                <span>Cancel</span>
              </button>
            )}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="flex border-b border-border">
          {['overview', 'documents', 'notes']?.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {tab}
            </button>
          ))}
        </div>

        <div className="p-4">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Job Title *</label>
                    <input type="text" value={editForm?.title} onChange={e => setEditForm(p => ({ ...p, title: e?.target?.value }))} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Service Type</label>
                      <select value={editForm?.service_type} onChange={e => setEditForm(p => ({ ...p, service_type: e?.target?.value }))} className={inputCls}>
                        <option value="">— None —</option>
                        {(() => {
                          const orgServiceTypes = organization?.settings?.service_types;
                          const options = orgServiceTypes?.length > 0
                            ? orgServiceTypes
                            : ['Plumbing','HVAC','Electrical','Cleaning','Landscaping','Pest Control','Roofing','Painting','Pool Service','Moving','General Contractor','Handyman','General','Other','local_move','long_distance','commercial_move','junk_removal','storage'];
                          // Include current value if not in list
                          const allOptions = editForm?.service_type && !options?.includes(editForm?.service_type)
                            ? [editForm?.service_type, ...options]
                            : options;
                          return allOptions?.map(s => <option key={s} value={s}>{s}</option>);
                        })()}
                      </select>
                    </div>
                    {organization?.business_type === 'moving' ? (
                      <div className="col-span-1">
                        {/* placeholder to keep grid alignment */}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Location</label>
                        <input type="text" value={editForm?.location} onChange={e => setEditForm(p => ({ ...p, location: e?.target?.value }))} className={inputCls} />
                      </div>
                    )}
                  </div>
                  {organization?.business_type === 'moving' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Pickup Address</label>
                          <input type="text" value={editForm?.pickup_address} onChange={e => setEditForm(p => ({ ...p, pickup_address: e?.target?.value }))} className={inputCls} placeholder="Origin address" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Delivery Address</label>
                          <input type="text" value={editForm?.delivery_address} onChange={e => setEditForm(p => ({ ...p, delivery_address: e?.target?.value }))} className={inputCls} placeholder="Destination address" />
                        </div>
                      </div>
                      {/* Move Type: Local vs Long Distance */}
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Move Type</label>
                        <div className="flex rounded-lg border border-border overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setEditForm(p => ({ ...p, move_type: 'local' }))}
                            className={`flex-1 py-1.5 text-xs font-medium transition-colors ${editForm?.move_type !== 'long_distance' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'}`}
                          >
                            🏠 Local Move
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditForm(p => ({ ...p, move_type: 'long_distance' }))}
                            className={`flex-1 py-1.5 text-xs font-medium transition-colors ${editForm?.move_type === 'long_distance' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'}`}
                          >
                            🚛 Long Distance
                          </button>
                        </div>
                      </div>
                      {/* CF and Miles — only for Long Distance */}
                      {editForm?.move_type === 'long_distance' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Cubic Feet (CF)</label>
                            <input type="number" min="0" step="1" value={editForm?.cubic_feet} onChange={e => setEditForm(p => ({ ...p, cubic_feet: e?.target?.value }))} className={inputCls} placeholder="e.g. 500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Miles</label>
                            <input type="number" min="0" step="1" value={editForm?.miles} onChange={e => setEditForm(p => ({ ...p, miles: e?.target?.value }))} className={inputCls} placeholder="e.g. 350" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date</label>
                      <input type="date" value={editForm?.scheduled_date} onChange={e => setEditForm(p => ({ ...p, scheduled_date: e?.target?.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Time</label>
                      <input type="time" value={editForm?.scheduled_time} onChange={e => setEditForm(p => ({ ...p, scheduled_time: e?.target?.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Duration (h)</label>
                      <input type="number" min="0.5" step="0.5" value={editForm?.duration_hours} onChange={e => setEditForm(p => ({ ...p, duration_hours: e?.target?.value }))} className={inputCls} />
                    </div>
                  </div>
                  <div className="border border-border rounded-lg p-3 space-y-3">
                    <p className="text-xs font-semibold text-foreground flex items-center">
                      <Icon name="DollarSign" size={13} className="mr-1.5 text-primary" />
                      Pricing
                    </p>

                    {/* Pricing type toggle for Moving */}
                    {isMovingBiz && (
                      <div className="flex rounded-lg border border-border overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setEditForm(p => ({ ...p, pricing_type: 'flat_rate' }))}
                          className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                            editForm?.pricing_type !== 'hourly_rate' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Flat Rate
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditForm(p => ({ ...p, pricing_type: 'hourly_rate' }))}
                          className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                            editForm?.pricing_type === 'hourly_rate' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Hourly Rate
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      {isHourlyRate ? (
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Rate per Hour ($)</label>
                          <input type="number" min="0" step="0.01" value={editForm?.hourly_rate}
                            onChange={e => setEditForm(p => ({ ...p, hourly_rate: e?.target?.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Service Price ($)</label>
                          <input type="number" min="0" step="0.01" value={editForm?.labor_cost}
                            onChange={e => setEditForm(p => ({ ...p, labor_cost: e?.target?.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Materials Cost ($)</label>
                        <input type="number" min="0" step="0.01" value={editForm?.materials_cost}
                          onChange={e => setEditForm(p => ({ ...p, materials_cost: e?.target?.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                      </div>
                    </div>

                    {/* Hourly rate summary */}
                    {isHourlyRate && editHourlyRate > 0 && (
                      <div className="bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-400">
                        ${editHourlyRate?.toFixed(2)}/hr × {editHours}h = <strong>${editComputedServicePrice?.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* Tax toggle */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <div
                          onClick={() => setEditForm(p => ({ ...p, tax_included: !p?.tax_included }))}
                          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                            editForm?.tax_included ? 'bg-primary' : 'bg-muted-foreground/30'
                          }`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow transition-transform ${
                            editForm?.tax_included ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                        </div>
                        <span className="text-xs font-medium text-foreground">Apply Tax</span>
                      </label>
                      {editForm?.tax_included && (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number" min="0" max="100" step="0.5"
                            value={editForm?.tax_rate}
                            onChange={e => setEditForm(p => ({ ...p, tax_rate: e?.target?.value }))}
                            className="w-16 px-2 py-1 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background text-center"
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>

                    {/* Live price breakdown */}
                    {(editComputedServicePrice > 0 || editMaterialsCost > 0) && (
                      <div className="bg-muted/50 rounded-lg p-2.5 space-y-1 text-xs">
                        <div className="flex justify-between text-muted-foreground">
                          <span>{isHourlyRate ? 'Labor (hourly)' : 'Service Price'}</span><span>${editComputedServicePrice?.toFixed(2)}</span>
                        </div>
                        {editMaterialsCost > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Materials</span><span>${editMaterialsCost?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-muted-foreground border-t border-border pt-1">
                          <span>Subtotal</span><span>${editComputedSubtotal?.toFixed(2)}</span>
                        </div>
                        {editForm?.tax_included && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Tax ({editForm?.tax_rate}%)</span><span>${editComputedTaxAmt?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold text-foreground border-t border-border pt-1">
                          <span>Total</span><span className="text-primary">${editComputedTotal?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</label>
                    <textarea value={editForm?.description} onChange={e => setEditForm(p => ({ ...p, description: e?.target?.value }))} rows={2} className={`${inputCls} resize-none`} placeholder="Add description..." />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Notes</label>
                    <textarea value={editForm?.notes} onChange={e => setEditForm(p => ({ ...p, notes: e?.target?.value }))} rows={8} className={`${inputCls} resize-none`} placeholder="Add notes about this job..." />
                  </div>
                  <button
                    onClick={handleSaveJob}
                    disabled={saving || !editForm?.title?.trim()}
                    className="w-full flex items-center justify-center space-x-2 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Icon name="Save" size={15} />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-muted-foreground">Service Type</p><p className="text-sm font-medium">{job?.service_type}</p></div>
                    <div><p className="text-xs text-muted-foreground">Scheduled Date</p><p className="text-sm font-medium">{job?.scheduled_date || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Location</p><p className="text-sm font-medium">{job?.location || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Duration</p><p className="text-sm font-medium">{job?.duration_hours || 1}h</p></div>
                    <div><p className="text-xs text-muted-foreground">Service Price</p><p className="text-sm font-medium">${Number(job?.labor_cost || 0)?.toFixed(2)}</p></div>
                    <div><p className="text-xs text-muted-foreground">Materials Cost</p><p className="text-sm font-medium">${Number(job?.materials_cost || 0)?.toFixed(2)}</p></div>
                  </div>
                  {/* Show pickup/delivery from custom_fields if present */}
                  {(job?.custom_fields?.pickup_address || job?.custom_fields?.delivery_address) && (
                    <div className="grid grid-cols-1 gap-2">
                      {job?.custom_fields?.pickup_address && (
                        <div><p className="text-xs text-muted-foreground">Pickup Address</p><p className="text-sm font-medium">{job?.custom_fields?.pickup_address}</p></div>
                      )}
                      {job?.custom_fields?.delivery_address && (
                        <div><p className="text-xs text-muted-foreground">Delivery Address</p><p className="text-sm font-medium">{job?.custom_fields?.delivery_address}</p></div>
                      )}
                      {/* Long Distance fields */}
                      {job?.move_type === 'long_distance' && (
                        <div className="flex items-center space-x-4 mt-1 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                          <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">🚛 Long Distance</span>
                          {job?.cubic_feet && (
                            <span className="text-xs text-foreground font-medium flex items-center space-x-1">
                              <Icon name="Package" size={11} className="text-muted-foreground" />
                              <span>{job?.cubic_feet} CF</span>
                            </span>
                          )}
                          {job?.miles && (
                            <span className="text-xs text-foreground font-medium flex items-center space-x-1">
                              <Icon name="Route" size={11} className="text-muted-foreground" />
                              <span>{job?.miles} mi</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {job?.description && (
                    <div><p className="text-xs text-muted-foreground mb-1">Description</p><p className="text-sm text-foreground whitespace-pre-wrap">{job?.description}</p></div>
                  )}
                  <div className="bg-primary/5 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Total Cost</p>
                    <p className="text-xl font-bold text-primary">${Number(job?.total_cost || 0)?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Change Status</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STATUS_CONFIG)?.map(([key, cfg]) => (
                        <button key={key} onClick={() => onStatusChange(job?.id, key)}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                            job?.status === key ? cfg?.color + ' ring-2 ring-offset-1 ring-current' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}>
                          {cfg?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-3">
              {loadingDocs ? (
                <div className="animate-pulse space-y-2">{[1,2,3]?.map(i => <div key={i} className="h-16 bg-muted rounded-lg" />)}</div>
              ) : documents?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No documents generated yet.</p>
              ) : (
                documents?.map(doc => (
                  <div key={doc?.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name={
                          doc?.doc_type === 'invoice' ? 'Receipt' :
                          doc?.doc_type === 'estimate' ? 'Calculator' :
                          doc?.doc_type === 'vehicle_inspection' ? 'ClipboardCheck' :
                          doc?.doc_type === 'work_order' ? 'Wrench' :
                          doc?.doc_type === 'contract' ? 'FileSignature' :
                          doc?.doc_type === 'service_agreement'? 'FileCheck' : 'FileText'
                        } size={16} className="text-primary" />
                        <span className="text-sm font-medium capitalize">
                          {doc?.doc_type === 'vehicle_inspection' ? 'Vehicle Inspection' :
                           doc?.doc_type === 'work_order' ? 'Work Order' :
                           doc?.doc_type === 'service_agreement' ? 'Service Agreement' :
                           doc?.doc_type === 'contract' ? 'Contract' :
                           doc?.doc_type}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          doc?.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          doc?.status === 'unpaid' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-muted text-muted-foreground'
                        }`}>{doc?.status}</span>
                      </div>
                    </div>
                    {selectedDoc?.id === doc?.id && (
                      <div className="mb-3 p-3 bg-muted rounded-lg text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {Object.entries(doc?.content)?.map(([k, v]) => (
                          <div key={k}><strong>{k?.replace(/_/g, ' ')}:</strong> {v}</div>
                        ))}
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <button onClick={() => setSelectedDoc(selectedDoc?.id === doc?.id ? null : doc)}
                        className="flex-1 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors">
                        {selectedDoc?.id === doc?.id ? 'Hide' : 'View'}
                      </button>
                      <button onClick={() => printDocument(doc)}
                        className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                        Print/PDF
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            isEditing ? (
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Notes</label>
                <textarea value={editForm?.notes} onChange={e => setEditForm(p => ({ ...p, notes: e?.target?.value }))} rows={8} className={`${inputCls} resize-none`} placeholder="Add notes about this job..." />
                <button
                  onClick={handleSaveJob}
                  disabled={saving}
                  className="mt-3 w-full flex items-center justify-center space-x-2 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  <Icon name="Save" size={15} />
                  <span>{saving ? 'Saving...' : 'Save Notes'}</span>
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{job?.notes || 'No notes added.'}</p>
              </div>
            )
          )}
        </div>

        {/* Invoice Builder Modal */}
        <InvoiceBuilder
          job={job}
          isOpen={isInvoiceBuilderOpen}
          onClose={() => setIsInvoiceBuilderOpen(false)}
          orgName={orgName}
          orgPhone={orgPhone}
          orgEmail={orgEmail}
        />
      </div>
    </div>
  );
};

const JobsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactData, setNewContactData] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const { profile, organization } = useAuth();

  const getDefaultServiceType = () => {
    if (!organization?.business_type) return 'General';
    const bt = organization?.business_type;
    if (bt === 'mobile_detailing') return 'Mobile Detailing';
    const mapped = bt?.charAt(0)?.toUpperCase() + bt?.slice(1)?.replace(/_/g, ' ');
    const match = Object.keys(JOB_SERVICE_FIELDS)?.find(s => s?.toLowerCase() === mapped?.toLowerCase() || s?.toLowerCase()?.includes(bt?.toLowerCase()?.split('_')?.[0]));
    return match || 'General';
  };

  const [newJob, setNewJob] = useState({
    title: '', client_id: '', service_type: 'General', description: '',
    location: '', scheduled_date: '', scheduled_time: '', duration_hours: 1,
    assigned_to: '', service_price: '', materials_cost: '',
    tax_included: false, tax_rate: 10,
    notes: '', service_fields: {}
  });

  useEffect(() => {
    if (organization?.business_type) {
      setNewJob(p => ({ ...p, service_type: getDefaultServiceType() }));
      // Reset service filter when business type changes so stale filter values are cleared
      setFilterService('');
    }
  }, [organization?.business_type]);

  const serviceTypeOptions = (() => {
    if (organization?.settings?.service_types?.length > 0) return organization?.settings?.service_types;
    if (organization?.business_type === 'mobile_detailing') {
      return ['Basic Wash', 'Full Detail', 'Interior Detail', 'Exterior Detail', 'Paint Correction', 'Ceramic Coating', 'Paint Protection Film', 'Engine Bay Detail', 'Headlight Restoration', 'Odor Elimination', 'Fleet Detail', 'Showroom Prep', 'Mobile Detailing', 'Other'];
    }
    return ['Plumbing', 'HVAC', 'Electrical', 'Handyman', 'Cleaning', 'Landscaping', 'Pest Control', 'Roofing', 'Painting', 'Pool Service', 'Moving', 'General Contractor', 'Mobile Detailing', 'General', 'Other'];
  })();

  // Moving / hourly-rate helpers for the Add Job modal
  const isMovingBiz = organization?.business_type === 'moving';
  const isHourlyRate = isMovingBiz && newJob?.pricing_type === 'hourly_rate';

  // Computed pricing
  let servicePrice = Number(newJob?.service_price) || 0;
  if (isHourlyRate) {
    servicePrice = (Number(newJob?.hourly_rate) || 0) * (Number(newJob?.duration_hours) || 1);
  }
  const materialsCost = Number(newJob?.materials_cost) || 0;
  const subtotal = servicePrice + materialsCost;
  const taxAmount = newJob?.tax_included ? subtotal * ((Number(newJob?.tax_rate) || 10) / 100) : 0;
  const totalCost = subtotal + taxAmount;

  const fetchJobs = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      let query = supabase?.from('jobs')?.select('*, client:contacts(first_name, last_name, email, phone), assigned:profiles(full_name)')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false });
      if (filterStatus) query = query?.eq('status', filterStatus);
      if (filterService) query = query?.eq('service_type', filterService);
      const { data, error } = await query;
      if (error) throw error;
      setJobs(data || []);
    } catch (err) {
      toast?.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, filterStatus, filterService]);

  const fetchContacts = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('contacts')?.select('id, first_name, last_name, email, phone')?.eq('org_id', profile?.org_id)?.is('deleted_at', null);
    if (data) setContacts(data);
  }, [profile?.org_id]);

  const fetchTeam = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('profiles')?.select('id, full_name')?.eq('org_id', profile?.org_id);
    if (data) setTeamMembers(data);
  }, [profile?.org_id]);

  useEffect(() => { fetchJobs(); fetchContacts(); fetchTeam(); }, [fetchJobs, fetchContacts, fetchTeam]);

  const handleClientSearchChange = (value) => {
    setClientSearch(value);
    setSelectedContact(null);
    setNewJob(p => ({ ...p, client_id: '' }));
    if (value?.trim()?.length > 0) {
      const lower = value?.toLowerCase();
      const filtered = contacts?.filter(c =>
        `${c?.first_name} ${c?.last_name}`?.toLowerCase()?.includes(lower) ||
        c?.email?.toLowerCase()?.includes(lower) ||
        c?.phone?.includes(value)
      );
      setClientSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setClientSuggestions([]);
      setShowSuggestions(false);
      setShowNewContactForm(false);
    }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setClientSearch(`${contact?.first_name} ${contact?.last_name}`);
    setNewJob(p => ({ ...p, client_id: contact?.id }));
    setShowSuggestions(false);
    setShowNewContactForm(false);
  };

  const handleCreateNewContact = async () => {
    if (!newContactData?.first_name?.trim()) return;
    setSaving(true);
    try {
      const { data, error } = await supabase?.from('contacts')?.insert({
        org_id: profile?.org_id,
        first_name: newContactData?.first_name?.trim(),
        last_name: newContactData?.last_name?.trim(),
        email: newContactData?.email?.trim(),
        phone: newContactData?.phone?.trim(),
        owner_id: profile?.id
      })?.select()?.single();
      if (error) throw error;
      setContacts(prev => [...prev, data]);
      handleSelectContact(data);
      setShowNewContactForm(false);
      setNewContactData({ first_name: '', last_name: '', email: '', phone: '' });
      toast?.success('Contact created!');
    } catch (err) {
      toast?.error('Failed to create contact: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const resetAddJobModal = () => {
    setNewJob({
      title: '', client_id: '', service_type: getDefaultServiceType(), description: '',
      location: '', scheduled_date: '', scheduled_time: '', duration_hours: 1,
      assigned_to: '', service_price: '', materials_cost: '',
      tax_included: false, tax_rate: 10,
      notes: '', service_fields: {}
    });
    setClientSearch('');
    setSelectedContact(null);
    setShowSuggestions(false);
    setShowNewContactForm(false);
    setNewContactData({ first_name: '', last_name: '', email: '', phone: '' });
    setIsAddModalOpen(false);
  };

  const handleAddJob = async () => {
    if (!newJob?.title?.trim()) return;
    setSaving(true);
    try {
      let clientId = newJob?.client_id || null;
      let contactRecord = null;

      if (clientId) {
        const { data } = await supabase?.from('contacts')?.select('*')?.eq('id', clientId)?.single();
        contactRecord = data;
      }

      const taxRate = newJob?.tax_included ? (Number(newJob?.tax_rate) || 10) : 0;
      const computedSubtotal = servicePrice + materialsCost;
      const computedTax = computedSubtotal * (taxRate / 100);
      const computedTotal = computedSubtotal + computedTax;

      const { data: jobData, error: jobError } = await supabase?.from('jobs')?.insert({
        org_id: profile?.org_id,
        title: newJob?.title,
        client_id: clientId,
        service_type: newJob?.service_type,
        description: newJob?.description,
        location: newJob?.location,
        scheduled_date: newJob?.scheduled_date || null,
        scheduled_time: newJob?.scheduled_time ? (newJob?.scheduled_time?.length === 5 ? newJob?.scheduled_time + ':00' : newJob?.scheduled_time) : null,
        duration_hours: Number(newJob?.duration_hours) || 1,
        assigned_to: newJob?.assigned_to || null,
        labor_cost: servicePrice,
        materials_cost: materialsCost,
        subtotal: computedSubtotal,
        total_cost: computedTotal,
        tax_included: newJob?.tax_included,
        tax_rate: taxRate,
        notes: newJob?.notes,
        status: 'scheduled',
        custom_fields: newJob?.service_fields || {}
      })?.select()?.single();
      if (jobError) throw jobError;

      // Auto-create calendar event
      if (newJob?.scheduled_date) {
        try {
          const startDt = newJob?.scheduled_time
            ? `${newJob?.scheduled_date}T${newJob?.scheduled_time}:00`
            : `${newJob?.scheduled_date}T09:00:00`;
          const endDt = new Date(new Date(startDt).getTime() + (Number(newJob.duration_hours) || 1) * 3600000)?.toISOString();
          await supabase?.from('calendar_events')?.insert({
            org_id: organization?.id || job?.org_id,
            job_id: job?.id,
            title: newJob?.title,
            description: newJob?.description || '',
            start_datetime: startDt,
            end_datetime: endDt,
            event_type: 'job',
            color: '#3B82F6'
          });
        } catch (_calErr) {
          // calendar event creation failure should not block job creation
        }
      }

      // Auto-generate documents
      try {
        const jobForDocs = { ...newJob, service_price: servicePrice, tax_included: newJob?.tax_included, tax_rate: taxRate };
        await generateDocuments(supabase, profile?.org_id, jobData?.id, jobForDocs, contactRecord, organization?.name || 'Wilcart', organization?.business_type);
      } catch (_docErr) {
        // document generation failure should not block job creation
      }

      // Auto-log activity
      try {
        const activityDueDate = newJob?.scheduled_date
          ? new Date(`${newJob.scheduled_date}T${newJob.scheduled_time || '09:00'}:00`)?.toISOString()
          : new Date()?.toISOString();
        await supabase?.from('activities')?.insert({
          org_id: profile?.org_id,
          owner_id: profile?.id,
          type: 'task',
          subject: `Job Created: ${newJob?.title}`,
          description: `New job created${contactRecord ? ` for ${contactRecord?.first_name} ${contactRecord?.last_name}` : ''}${newJob?.service_type ? ` — ${newJob?.service_type}` : ''}`,
          due_date: activityDueDate,
          priority: 'medium',
          status: 'pending'
        });
      } catch (_actErr) {
        // activity logging failure should not block job creation
      }

      toast?.success('Job created!');
      resetAddJobModal();
      fetchJobs();
    } catch (err) {
      toast?.error('Failed to create job: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await supabase?.from('jobs')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', jobId);
      toast?.success('Moved to Trash');
      fetchJobs();
      if (selectedJob?.id === jobId) setIsDrawerOpen(false);
    } catch (err) {
      toast?.error('Failed to delete');
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await supabase?.from('jobs')?.update({ status: newStatus })?.eq('id', jobId);
      setJobs(prev => prev?.map(j => j?.id === jobId ? { ...j, status: newStatus } : j));
      if (selectedJob?.id === jobId) setSelectedJob(prev => ({ ...prev, status: newStatus }));
      toast?.success('Status updated');

      // In-app notification for job status changes
      const notifType = newStatus === 'completed' ? 'job_completed' : 'job_update';
      const jobTitle = selectedJob?.title || selectedJob?.name || 'Job';
      await createInAppNotification({
        org_id: profile?.org_id,
        type: notifType,
        title: newStatus === 'completed' ? `Job Completed: ${jobTitle}` : `Job Updated: ${jobTitle}`,
        body: `Status changed to ${newStatus?.replace('_', ' ')}`,
        link: '/jobs',
      });

      // SMS notification to client if enabled
      try {
        if (organization?.sms_enabled && organization?.sms_notify_job_status) {
          const job = jobs?.find(j => j?.id === jobId) || selectedJob;
          const clientPhone = job?.client?.phone;
          if (clientPhone) {
            const clientName = job?.client?.first_name
              ? `${job?.client?.first_name} ${job?.client?.last_name || ''}`?.trim()
              : null;
            const message = buildJobStatusSMS({
              clientName,
              jobTitle: job?.title || 'your job',
              newStatus,
              orgName: organization?.name,
            });
            await sendSMS({
              to: clientPhone,
              message,
              orgId: profile?.org_id,
              eventType: 'job_status_change',
            });
          }
        }
      } catch (_smsErr) {
        // SMS failure should not block status update
      }
    } catch (err) {
      toast?.error('Failed to update status');
    }
  };

  const filteredJobs = jobs?.filter(j => {
    if (!searchTerm) return true;
    const term = searchTerm?.toLowerCase();
    return j?.title?.toLowerCase()?.includes(term) ||
      j?.client?.first_name?.toLowerCase()?.includes(term) ||
      j?.client?.last_name?.toLowerCase()?.includes(term);
  });

  const currentServiceConfig = getServiceConfig(newJob?.service_type, organization?.business_type);
  const hideLocation = currentServiceConfig?.hideLocation === true;

  return (
    <>
      <Helmet><title>Jobs - Wilcart CRM</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Jobs</h1>
                <p className="text-muted-foreground mt-1">{filteredJobs?.length} jobs</p>
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="mt-4 sm:mt-0">
                <Icon name="Plus" size={16} className="mr-2" />
                Add Job
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={e => setSearchTerm(e?.target?.value)}
                  className="pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                <option value="">All Statuses</option>
                {Object.entries(STATUS_CONFIG)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
              </select>
              <select value={filterService} onChange={e => setFilterService(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                <option value="">All Services</option>
                {serviceTypeOptions?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="space-y-3">{[1,2,3,4,5]?.map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>
            ) : filteredJobs?.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="Briefcase" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No jobs found. Create your first job!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJobs?.map(job => {
                  const statusCfg = STATUS_CONFIG?.[job?.status] || STATUS_CONFIG?.scheduled;
                  const clientName = job?.client ? `${job?.client?.first_name} ${job?.client?.last_name}`?.trim() : 'No client';
                  return (
                    <div key={job?.id} onClick={() => { setSelectedJob(job); setIsDrawerOpen(true); }}
                      className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-semibold text-foreground truncate">{job?.title}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium flex-shrink-0 ${statusCfg?.color}`}>{statusCfg?.label}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center"><Icon name="User" size={12} className="mr-1" />{clientName}</span>
                            <span className="flex items-center"><Icon name="Wrench" size={12} className="mr-1" />{job?.service_type}</span>
                            {job?.scheduled_date && <span className="flex items-center"><Icon name="Calendar" size={12} className="mr-1" />{job?.scheduled_date}</span>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <span className="text-sm font-semibold text-foreground">${Number(job?.total_cost || 0)?.toFixed(2)}</span>
                          <button onClick={e => { e?.stopPropagation(); handleDeleteJob(job?.id); }}
                            className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* Add Job Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{currentServiceConfig?.icon || '📋'}</span>
                  <h2 className="text-lg font-semibold text-foreground">Add Job</h2>
                </div>
                <button onClick={resetAddJobModal} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Job Title *</label>
                  <input type="text" value={newJob?.title} onChange={e => setNewJob(p => ({ ...p, title: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>

                {/* Client / Contact field */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Client / Contact</label>
                  <div className="relative">
                    <div className="relative">
                      <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={e => handleClientSearchChange(e?.target?.value)}
                        onFocus={() => { if (clientSearch?.trim() && clientSuggestions?.length > 0) setShowSuggestions(true); }}
                        placeholder="Search existing contacts..."
                        className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background ${
                          selectedContact ? 'border-green-400 bg-green-100 dark:bg-green-900/30' : 'border-border'
                        }`}
                      />
                      {selectedContact && (
                        <button
                          onClick={() => { setSelectedContact(null); setClientSearch(''); setNewJob(p => ({ ...p, client_id: '' })); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      )}
                    </div>

                    {/* Suggestions dropdown */}
                    {showSuggestions && !selectedContact && (
                      <div className="absolute z-20 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clientSuggestions?.length > 0 ? (
                          clientSuggestions?.map(c => (
                            <button key={c?.id} onMouseDown={() => handleSelectContact(c)}
                              className="w-full flex items-center px-3 py-2.5 hover:bg-muted text-left transition-colors">
                              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mr-2.5 flex-shrink-0">
                                <span className="text-xs font-semibold text-primary">{c?.first_name?.[0]}{c?.last_name?.[0]}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{c?.first_name} {c?.last_name}</p>
                                {c?.email && <p className="text-xs text-muted-foreground truncate">{c?.email}</p>}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No contacts found</div>
                        )}
                        <button
                          onMouseDown={() => { setShowSuggestions(false); setShowNewContactForm(true); }}
                          className="w-full flex items-center px-3 py-2.5 hover:bg-primary/5 text-primary text-sm font-medium border-t border-border transition-colors"
                        >
                          <Icon name="UserPlus" size={14} className="mr-2" />
                          Create new contact
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Selected contact badge */}
                  {selectedContact && (
                    <div className="mt-1.5 flex items-center space-x-2 text-xs text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg px-2.5 py-1.5">
                      <Icon name="UserCheck" size={13} />
                      <span>{selectedContact?.first_name} {selectedContact?.last_name}</span>
                      {selectedContact?.phone && <span className="text-green-600">· {selectedContact?.phone}</span>}
                    </div>
                  )}

                  {/* No contact selected — show create button */}
                  {!selectedContact && !showNewContactForm && !clientSearch && (
                    <button
                      onClick={() => setShowNewContactForm(true)}
                      className="mt-1.5 flex items-center text-xs text-primary hover:underline"
                    >
                      <Icon name="UserPlus" size={12} className="mr-1" />
                      Create new contact
                    </button>
                  )}

                  {/* Inline new contact form */}
                  {showNewContactForm && (
                    <div className="mt-2 p-3 border border-primary/30 bg-primary/5 rounded-lg space-y-2">
                      <p className="text-xs font-semibold text-primary flex items-center">
                        <Icon name="UserPlus" size={12} className="mr-1" />
                        New Contact
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="First name *"
                          value={newContactData?.first_name}
                          onChange={e => setNewContactData(p => ({ ...p, first_name: e?.target?.value }))}
                          className="px-2.5 py-1.5 border border-border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                        />
                        <input
                          type="text"
                          placeholder="Last name"
                          value={newContactData?.last_name}
                          onChange={e => setNewContactData(p => ({ ...p, last_name: e?.target?.value }))}
                          className="px-2.5 py-1.5 border border-border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                        />
                      </div>
                      <input
                        type="email"
                        placeholder="Email"
                        value={newContactData?.email}
                        onChange={e => setNewContactData(p => ({ ...p, email: e?.target?.value }))}
                        className="w-full px-2.5 py-1.5 border border-border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                      />
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={newContactData?.phone}
                        onChange={e => setNewContactData(p => ({ ...p, phone: e?.target?.value }))}
                        className="w-full px-2.5 py-1.5 border border-border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                      />
                      <div className="flex space-x-2 pt-1">
                        <button
                          onClick={() => { setShowNewContactForm(false); setNewContactData({ first_name: '', last_name: '', email: '', phone: '' }); }}
                          className="flex-1 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateNewContact}
                          disabled={!newContactData?.first_name?.trim() || saving}
                          className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save Contact'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Service Type</label>
                    <select value={newJob?.service_type} onChange={e => setNewJob(p => ({ ...p, service_type: e?.target?.value, service_fields: {} }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      {serviceTypeOptions?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Assign To</label>
                    <select value={newJob?.assigned_to} onChange={e => setNewJob(p => ({ ...p, assigned_to: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      <option value="">Unassigned</option>
                      {teamMembers?.map(m => <option key={m?.id} value={m?.id}>{m?.full_name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Dynamic service-specific fields */}
                {currentServiceConfig && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                    <p className="text-xs font-semibold text-primary flex items-center">
                      <span className="mr-1.5">{currentServiceConfig?.icon}</span>
                      {newJob?.service_type} — Job Details
                    </p>
                    <div className="grid grid-cols-1 gap-3">
                      {currentServiceConfig?.fields?.map(field => (
                        <div key={field?.key}>
                          <label className="block text-xs font-medium text-foreground mb-1">
                            {field?.label}{field?.required && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          {field?.type === 'select' ? (
                            <select
                              value={newJob?.service_fields?.[field?.key] || ''}
                              onChange={e => setNewJob(p => ({ ...p, service_fields: { ...p?.service_fields, [field?.key]: e?.target?.value } }))}
                              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                            >
                              <option value="">Select...</option>
                              {field?.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          ) : (
                            <input
                              type={field?.type}
                              placeholder={field?.placeholder || ''}
                              value={newJob?.service_fields?.[field?.key] || ''}
                              onChange={e => setNewJob(p => ({ ...p, service_fields: { ...p?.service_fields, [field?.key]: e?.target?.value } }))}
                              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea value={newJob?.description} onChange={e => setNewJob(p => ({ ...p, description: e?.target?.value }))}
                    rows={2} className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" />
                </div>

                {/* Location — hidden for Moving (uses move_from/move_to instead) */}
                {!hideLocation && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Location</label>
                    <input type="text" value={newJob?.location} onChange={e => setNewJob(p => ({ ...p, location: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Date</label>
                    <input type="date" value={newJob?.scheduled_date} onChange={e => setNewJob(p => ({ ...p, scheduled_date: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Time</label>
                    <input type="time" value={newJob?.scheduled_time} onChange={e => setNewJob(p => ({ ...p, scheduled_time: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Duration (h)</label>
                    <input type="number" min="0.5" step="0.5" value={newJob?.duration_hours} onChange={e => setNewJob(p => ({ ...p, duration_hours: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                </div>

                {/* Pricing section */}
                <div className="border border-border rounded-lg p-3 space-y-3">
                  <p className="text-xs font-semibold text-foreground flex items-center">
                    <Icon name="DollarSign" size={13} className="mr-1.5 text-primary" />
                    Pricing
                  </p>

                  {/* Pricing type toggle for Moving */}
                  {isMovingBiz && (
                    <div className="flex rounded-lg border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setNewJob(p => ({ ...p, pricing_type: 'flat_rate' }))}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          newJob?.pricing_type !== 'hourly_rate' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Flat Rate
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewJob(p => ({ ...p, pricing_type: 'hourly_rate' }))}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          newJob?.pricing_type === 'hourly_rate' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Hourly Rate
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {isHourlyRate ? (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Rate per Hour ($)</label>
                        <input type="number" min="0" step="0.01" value={newJob?.hourly_rate}
                          onChange={e => setNewJob(p => ({ ...p, hourly_rate: e?.target?.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Service Price ($)</label>
                        <input type="number" min="0" step="0.01" value={newJob?.service_price}
                          onChange={e => setNewJob(p => ({ ...p, service_price: e?.target?.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Materials Cost ($)</label>
                      <input type="number" min="0" step="0.01" value={newJob?.materials_cost}
                        onChange={e => setNewJob(p => ({ ...p, materials_cost: e?.target?.value }))}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-center" />
                    </div>
                  </div>

                  {/* Tax toggle */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <div
                        onClick={() => setNewJob(p => ({ ...p, tax_included: !p?.tax_included }))}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                          newJob?.tax_included ? 'bg-primary' : 'bg-muted-foreground/30'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow transition-transform ${
                          newJob?.tax_included ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </div>
                      <span className="text-xs font-medium text-foreground">Apply Tax</span>
                    </label>
                    {newJob?.tax_included && (
                      <div className="flex items-center space-x-1">
                        <input
                          type="number" min="0" max="100" step="0.5"
                          value={newJob?.tax_rate}
                          onChange={e => setNewJob(p => ({ ...p, tax_rate: e?.target?.value }))}
                          className="w-16 px-2 py-1 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-background text-center"
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                  </div>

                  {/* Live price breakdown */}
                  {(servicePrice > 0 || materialsCost > 0) && (
                    <div className="bg-muted/50 rounded-lg p-2.5 space-y-1 text-xs">
                      <div className="flex justify-between text-muted-foreground">
                        <span>{isHourlyRate ? 'Labor (hourly)' : 'Service Price'}</span><span>${servicePrice?.toFixed(2)}</span>
                      </div>
                      {materialsCost > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Materials</span><span>${materialsCost?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-muted-foreground border-t border-border pt-1">
                        <span>Subtotal</span><span>${subtotal?.toFixed(2)}</span>
                      </div>
                      {newJob?.tax_included && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Tax ({newJob?.tax_rate}%)</span><span>${taxAmount?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-foreground border-t border-border pt-1">
                        <span>Total</span><span className="text-primary">${totalCost?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
                  <textarea value={newJob?.notes} onChange={e => setNewJob(p => ({ ...p, notes: e?.target?.value }))}
                    rows={2} className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={resetAddJobModal}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddJob} loading={saving} disabled={saving || !newJob?.title?.trim()}>
                  {saving ? 'Creating...' : 'Create Job'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <JobDetailDrawer
          job={selectedJob}
          isOpen={isDrawerOpen}
          onClose={() => { setIsDrawerOpen(false); setSelectedJob(null); }}
          onStatusChange={handleStatusChange}
          orgName={organization?.name || 'Wilcart'}
          orgPhone={organization?.phone || ''}
          orgEmail={organization?.email || ''}
          organization={organization}
          onUpdate={(updatedJob) => {
            setJobs(prev => prev?.map(j => j?.id === updatedJob?.id ? { ...j, ...updatedJob } : j));
            setSelectedJob(prev => ({ ...prev, ...updatedJob }));
          }}
        />
      </div>
    </>
  );
};

export default JobsPage;
